import React, { useState, useEffect, useMemo, useRef } from 'react';
import Select from 'react-select';
import QRCode from 'qrcode';
import { firestore } from '../firebase';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  deleteDoc,
  doc,
  updateDoc,
  setDoc,
  query,
  where,
  writeBatch
} from 'firebase/firestore';
import EmailComposerModal from './EmailComposerModal';
import { useEmailComposer } from '../hooks/useEmailComposer';
import { openPrintWindow, getOperationalTablePrintCss } from '../utils/printUtils';
import { copyText, buildTsvTable } from '../utils/clipboardUtils';
import AdminCopyMenu from './admin/AdminCopyMenu';
import AdminColumnExportModal from './admin/AdminColumnExportModal';
import { collectInvalidNamedControlNames } from '../utils/collectInvalidNamedControlNames';
import ModalFieldRequiredError from './ModalFieldRequiredError';
import { isMentorEligibleForPairing } from '../utils/mentorEligibility';
import './TeamManagement.css';
import './admin/FmModal.css';
import { useModalEscapeKey } from '../utils/useModalEscapeKey';

function escapeHtmlCell(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function normalizeCsvHeaderCell(h) {
  return String(h ?? '')
    .replace(/^\ufeff/, '')
    .trim()
    .toLowerCase();
}

/** Index of the team # column after headers are normalized via normalizeCsvHeaderCell. */
function findTeamNumberColumnIndex(normalizedHeader) {
  return normalizedHeader.findIndex(
    (cell) =>
      cell === 'team number' ||
      cell === 'team num' ||
      cell === 'teamnum' ||
      cell === 'team' ||
      cell === 'team #' ||
      cell === 'team#' ||
      cell === '#'
  );
}

const TEAM_MANAGEMENT_EXPORT_COLS_MAIN = [
  { id: 'teamNumber', label: 'Team #', default: true },
  { id: 'teamName', label: 'Team name', default: true },
  { id: 'mentor', label: 'Mentor', default: true },
  { id: 'mentorEmail', label: 'Mentor email', default: false },
  { id: 'division', label: 'Division', default: true },
  { id: 'guest', label: 'Guest', default: true },
  { id: 'test', label: 'Test', default: false },
  { id: 'passcode', label: 'Passcode', default: false },
  { id: 'freshlings', label: 'Freshlings', default: true },
];

const TEAM_MANAGEMENT_EXPORT_COLS_UNREG = [
  { id: 'rowNum', label: '#', default: true },
  { id: 'mentor', label: 'Mentor', default: true },
  { id: 'teamNumber', label: 'Team #', default: true },
  { id: 'missing', label: 'Missing', default: true },
  { id: 'mentorEmail', label: 'Mentor email', default: false },
];

const TeamManagement = () => {
  // State for active events
  const [activeEvents, setActiveEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');

  // State for teams
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state for add/edit team
  const [modalOpen, setModalOpen] = useState(false);
  const [teamFormInvalidNames, setTeamFormInvalidNames] = useState(() => new Set());
  // Modal state for CSV upload
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  // Team form state (required: teamNumber, passcode; others optional)
  const [newTeam, setNewTeam] = useState({
    teamNumber: '',
    teamName: '',
    mentor: '',
    division: '',
    guest: false,
    test: false,
    passcode: '',
    freshlings: []   // NOW AN EMPTY ARRAY
  });
  // Holds document ID if editing an existing team
  const [editingTeamId, setEditingTeamId] = useState(null);

  // CSV file state
  const [csvFile, setCsvFile] = useState(null);
 
  // State for searchable dropdown options (mentors/freshlings from flyers)
  const [mentorOptions, setMentorOptions] = useState([]);
  const [freshlingOptions, setFreshlingOptions] = useState([]);
  const [showAllMentorFlyers, setShowAllMentorFlyers] = useState(false);

  // State for search
  const [searchQuery, setSearchQuery] = useState('');

  // State for column sorting
  const [sortBy, setSortBy] = useState('number'); // 'number' | 'mentor'
  const [sortDir, setSortDir] = useState('asc');   // 'asc' | 'desc'

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  };

  const { emailComposerProps, openEmailComposer, closeEmailComposer } = useEmailComposer();

  // Team passcodes (independent of teams; used for login)
  const [teamPasscodes, setTeamPasscodes] = useState({}); // { teamNum: passcode }
  const [passcodesFile, setPasscodesFile] = useState(null);
  const [uploadingPasscodes, setUploadingPasscodes] = useState(false);
  const [teamMgmtExport, setTeamMgmtExport] = useState(null); // { kind: 'main'|'unreg', mode } | null
  const [teamMgmtExportCols, setTeamMgmtExportCols] = useState(() => new Set());

  const passcodesFileInputRef = useRef(null);
  /** When set, shows confirm-before-email modal for registration reminders */
  const [registrationReminderModal, setRegistrationReminderModal] = useState(null);

  useEffect(() => {
    if (!modalOpen) setTeamFormInvalidNames(new Set());
  }, [modalOpen]);

  // Fetch active events from Firestore
  useEffect(() => {
    let cancelled = false;
    async function fetchEvents() {
      try {
        const [qs, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const eventsData = qs.docs.map((d) => ({ id: d.id, ...d.data() }));
        setActiveEvents(eventsData);
        if (eventsData.length > 0) {
          setSelectedEvent(
            resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events: eventsData })
          );
        }
      } catch (err) {
        if (!cancelled) setError('Failed to fetch events: ' + err.message);
      }
    }
    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch teams for the selected event
  useEffect(() => {
    async function fetchTeams() {
      if (!selectedEvent) return;
      try {
        const q = query(collection(firestore, 'teams'), where('eventId', '==', selectedEvent));
        const qs = await getDocs(q);
        const teamsData = qs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTeams(teamsData);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch teams: ' + err.message);
        setLoading(false);
      }
    }
    fetchTeams();
  }, [selectedEvent]);

  // Fetch team passcodes for selected event (only the passcodes map; no extra data)
  useEffect(() => {
    if (!selectedEvent) return;
    (async () => {
      try {
        const ref = doc(firestore, 'teamPasscodes', selectedEvent);
        const snap = await getDoc(ref);
        const data = snap.exists() ? snap.data() : {};
        const passcodes = data.passcodes && typeof data.passcodes === 'object' ? data.passcodes : {};
        setTeamPasscodes(passcodes);
      } catch (_) {
        setTeamPasscodes({});
      }
    })();
  }, [selectedEvent]);

  // Fetch mentor/freshling options from flyers
  useEffect(() => {
    async function fetchOptions() {
      if (!selectedEvent) return;
      try {
        // Mentor options
        const mentorQ = query(
          collection(firestore, 'flyers'),
          where('eventId', '==', selectedEvent),
          where('type', '==', 'mentor')
        );
        const mentorSnapshot = await getDocs(mentorQ);
        const mentorRows = mentorSnapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          const flyer = { id: docSnap.id, ...data };
          const name = data.name || data.email;
          return {
            value: data.email,
            label: (data.test ? '[Test] ' : '') + name,
            test: !!data.test,
            _eligible: isMentorEligibleForPairing(flyer),
          };
        });
        const mentors = (showAllMentorFlyers ? mentorRows : mentorRows.filter((r) => r._eligible)).map(
          ({ _eligible, ...opt }) => opt
        );
        setMentorOptions(mentors);

        // Freshling options
        const freshlingQ = query(
          collection(firestore, 'flyers'),
          where('eventId', '==', selectedEvent),
          where('type', '==', 'freshling')
        );
        const freshlingSnapshot = await getDocs(freshlingQ);
        const freshlings = freshlingSnapshot.docs.map(doc => {
          const data = doc.data();
          const name = data.name || data.email;
          return {
            value: data.email,
            label: (data.test ? '[Test] ' : '') + name,
            test: !!data.test
          };
        });
        setFreshlingOptions(freshlings);
      } catch (err) {
        console.error("Error fetching flyer options: ", err);
      }
    }
    fetchOptions();
  }, [selectedEvent, showAllMentorFlyers]);

  // Add/Edit team form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      setTeamFormInvalidNames(collectInvalidNamedControlNames(form));
      const inv = form.querySelector(':invalid');
      if (inv && typeof inv.focus === 'function') inv.focus();
      return;
    }
    setTeamFormInvalidNames(new Set());

    // Default empty values if not set
    const teamData = {
      teamNumber: newTeam.teamNumber,
      teamName: newTeam.teamName || '',  // Ensure teamName is defined
      mentor: newTeam.mentor || '',  // Ensure mentor is defined
      division: newTeam.division || '',  // Ensure division is defined
      guest: newTeam.guest !== undefined ? newTeam.guest : false,  // Ensure guest is a boolean (false if undefined)
      test: newTeam.test === true,  // Test team (dummy data for pre-event testing)
      passcode: newTeam.passcode || '',  // Ensure passcode is defined
      freshlings: Array.isArray(newTeam.freshlings) ? newTeam.freshlings : [], // Ensure freshlings is an array
      eventId: selectedEvent
    };
  
    try {
      const existingTeam = teams.find(t => t.teamNumber === newTeam.teamNumber);
      
      if (editingTeamId) {
        const teamDocRef = doc(firestore, 'teams', editingTeamId);
        await updateDoc(teamDocRef, teamData);
        setTeams(teams.map(t => (t.id === editingTeamId ? { id: editingTeamId, ...teamData } : t)));
        setEditingTeamId(null);
      } else if (existingTeam) {
        await updateDoc(doc(firestore, 'teams', existingTeam.id), teamData);
        setTeams(teams.map(t => (t.id === existingTeam.id ? { id: existingTeam.id, ...teamData } : t)));
      } else {
        const docRef = await addDoc(collection(firestore, 'teams'), teamData);
        setTeams([...teams, { id: docRef.id, ...teamData }]);
      }

      // Keep teamPasscodes in sync so login and table use the same passcode
      const numKey = String(newTeam.teamNumber ?? '').trim();
      if (numKey && (newTeam.passcode || '') !== '') {
        const updatedPasscodes = { ...teamPasscodes, [numKey]: newTeam.passcode || '' };
        await setDoc(doc(firestore, 'teamPasscodes', selectedEvent), { passcodes: updatedPasscodes }, { merge: false });
        setTeamPasscodes(updatedPasscodes);
      }
  
      // Reset form after submission
      setNewTeam({
        teamNumber: '',
        teamName: '',
        mentor: '',
        division: '',
        guest: false,
        test: false,
        passcode: '',
        freshlings: [],
      });
      setEditingTeamId(null);
      setModalOpen(false);
    } catch (err) {
      setError('Error saving team: ' + err.message);
    }
  };
  
  
  const handleDelete = async (teamId) => {
    try {
      await deleteDoc(doc(firestore, 'teams', teamId));
      setTeams(teams.filter(t => t.id !== teamId));
    } catch (err) {
      setError('Error deleting team: ' + err.message);
    }
  };

  const handleEdit = (team) => {
    setEditingTeamId(team.id);
    const teamNumKey = String(team.teamNumber ?? '').trim();
    const effectivePasscode = teamPasscodes[teamNumKey] ?? team.passcode ?? '';
    setNewTeam({
      teamNumber: team.teamNumber,
      teamName: team.teamName,
      mentor: team.mentor,
      division: team.division,
      guest: team.guest,
      test: team.test === true,
      passcode: effectivePasscode,
      freshlings: team.freshlings || []
    });
    setModalOpen(true);
  };

  const handleDeleteAll = async () => {
    if (window.confirm("Are you sure you want to delete all teams for this event?")) {
      try {
        const q = query(collection(firestore, 'teams'), where('eventId', '==', selectedEvent));
        const qs = await getDocs(q);
        const deletePromises = qs.docs.map(docSnap => deleteDoc(doc(firestore, 'teams', docSnap.id)));
        await Promise.all(deletePromises);
        setTeams([]);
      } catch (err) {
        setError('Error deleting all teams: ' + err.message);
      }
    }
  };

  const handleDeleteAllTestData = async () => {
    if (!selectedEvent) {
      setError('Please select an event first.');
      return;
    }
    if (!window.confirm('Delete all test teams, their scores and bonus scores, and all test flyers for this event? This cannot be undone.')) return;
    setError('');
    try {
      const teamsQ = query(
        collection(firestore, 'teams'),
        where('eventId', '==', selectedEvent),
        where('test', '==', true)
      );
      const teamsSnap = await getDocs(teamsQ);
      const testTeams = teamsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const testTeamIds = testTeams.map((t) => t.id);
      const testTeamNumbers = testTeams.map((t) => String(t.teamNumber ?? '').trim()).filter(Boolean);

      if (testTeamIds.length === 0) {
        const flyersQ = query(
          collection(firestore, 'flyers'),
          where('eventId', '==', selectedEvent),
          where('test', '==', true)
        );
        const flyersSnap = await getDocs(flyersQ);
        if (flyersSnap.empty) {
          setError('No test data found for this event.');
          return;
        }
      }

      const BATCH_SIZE = 500;

      const scoresQ = query(
        collection(firestore, 'scores'),
        where('eventId', '==', selectedEvent)
      );
      const scoresSnap = await getDocs(scoresQ);
      const scoreDocsToDelete = scoresSnap.docs.filter((d) => testTeamIds.includes(d.data().teamId));
      for (let i = 0; i < scoreDocsToDelete.length; i += BATCH_SIZE) {
        const batch = writeBatch(firestore);
        scoreDocsToDelete.slice(i, i + BATCH_SIZE).forEach((d) => batch.delete(d.ref));
        await batch.commit();
      }

      const bonusQ = query(
        collection(firestore, 'bonusScores'),
        where('eventId', '==', selectedEvent)
      );
      const bonusSnap = await getDocs(bonusQ);
      const bonusDocsToDelete = bonusSnap.docs.filter((d) => testTeamIds.includes(d.data().teamId));
      for (let i = 0; i < bonusDocsToDelete.length; i += BATCH_SIZE) {
        const batch = writeBatch(firestore);
        bonusDocsToDelete.slice(i, i + BATCH_SIZE).forEach((d) => batch.delete(d.ref));
        await batch.commit();
      }

      const passcodesRef = doc(firestore, 'teamPasscodes', selectedEvent);
      const passcodesSnap = await getDoc(passcodesRef);
      const passcodes = passcodesSnap.exists() && passcodesSnap.data().passcodes
        ? { ...passcodesSnap.data().passcodes }
        : {};
      testTeamNumbers.forEach((num) => delete passcodes[num]);
      await setDoc(passcodesRef, { passcodes }, { merge: false });
      setTeamPasscodes(passcodes);

      testTeams.forEach((t) => deleteDoc(doc(firestore, 'teams', t.id)));
      setTeams((prev) => prev.filter((t) => !testTeamIds.includes(t.id)));

      const flyersQ = query(
        collection(firestore, 'flyers'),
        where('eventId', '==', selectedEvent),
        where('test', '==', true)
      );
      const flyersSnap = await getDocs(flyersQ);
      for (let i = 0; i < flyersSnap.docs.length; i += BATCH_SIZE) {
        const batch = writeBatch(firestore);
        flyersSnap.docs.slice(i, i + BATCH_SIZE).forEach((d) => batch.delete(d.ref));
        await batch.commit();
      }

      setError('');
    } catch (err) {
      setError('Error deleting test data: ' + err.message);
    }
  };

  const openRegistrationReminderModal = () => {
    const incompleteTeams = teams.filter(
      (t) => !t.test && (!(t.teamName || '').trim() || !(t.division || '').trim())
    );
    if (incompleteTeams.length === 0) {
      alert('All teams have a name and division set. No reminders needed.');
      return;
    }

    const emails = new Set();
    incompleteTeams.forEach((t) => {
      if ((t.mentor || '').trim()) emails.add(t.mentor.trim());
    });

    if (emails.size === 0) {
      alert(`${incompleteTeams.length} team(s) are incomplete but none have email addresses assigned.`);
      return;
    }

    const missingName = incompleteTeams.filter((t) => !(t.teamName || '').trim());
    const missingDiv = incompleteTeams.filter((t) => !(t.division || '').trim());
    const summaryParts = [];
    if (missingName.length) summaryParts.push(`${missingName.length} missing a team name`);
    if (missingDiv.length) summaryParts.push(`${missingDiv.length} missing a division`);

    setRegistrationReminderModal({
      incompleteTeamCount: incompleteTeams.length,
      mentorEmailCount: emails.size,
      summaryParts,
      bccEmails: Array.from(emails),
    });
  };

  const confirmRegistrationReminder = () => {
    if (!registrationReminderModal) return;
    const { bccEmails } = registrationReminderModal;
    setRegistrationReminderModal(null);

    const body =
      `Good morning teams!\n\n` +
      `It looks like you have not completed registration (choose a team name and select a division). ` +
      `Please make sure this is done before registration closes so your team is all set for the event.\n\n` +
      `Register here: https://freshmeet-d75a0.web.app/register\n\n` +
      `If this was sent in error or you have technical difficulties, find us at registration and we'll sort you out.\n\n` +
      `Thanks, and see you soon!`;

    openEmailComposer({
      context: null,
      to: '',
      bcc: bccEmails.join(','),
      template: 'custom',
      subject: 'Reminder: Complete Your Fresh Meet Registration Before Saturday 8 AM',
      body,
    });
  };

  const teamMgmtMainExportRows = useMemo(() => {
    const TEAM_COUNT = 60;
    const allTeamNumbers = Array.from({ length: TEAM_COUNT }, (_, i) => i + 1);
    const rowMatchesSearch = (teamNumber, team) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();
      if (!team) return String(teamNumber).includes(q);
      const teamNameMatch = (team.teamName || '').toLowerCase().includes(q);
      const mentorOption = mentorOptions.find((opt) => opt.value === team.mentor);
      const mentorName = mentorOption ? mentorOption.label : '';
      const mentorMatch = mentorName.toLowerCase().includes(q);
      const freshlingNames = Array.isArray(team.freshlings)
        ? team.freshlings
            .map((email) => {
              const freshlingOption = freshlingOptions.find((opt) => opt.value === email);
              return freshlingOption ? freshlingOption.label : email;
            })
            .join(' ')
        : '';
      const freshlingMatch = freshlingNames.toLowerCase().includes(q);
      return teamNameMatch || mentorMatch || freshlingMatch || String(teamNumber).includes(q);
    };
    const getMentorName = (team) => {
      if (!team?.mentor) return '';
      const opt = mentorOptions.find((o) => o.value === team.mentor);
      return (opt ? opt.label : team.mentor).toLowerCase();
    };
    const normalizeTeamNumber = (value) => String(value ?? '').trim();
    const numberedRangeSet = new Set(allTeamNumbers.map((n) => String(n)));
    const teamsByNumber = new Map();
    const unnumberedTeams = [];
    teams.forEach((team) => {
      const normalized = normalizeTeamNumber(team?.teamNumber);
      if (numberedRangeSet.has(normalized)) {
        if (!teamsByNumber.has(normalized)) teamsByNumber.set(normalized, team);
      } else {
        unnumberedTeams.push(team);
      }
    });

    const visibleRows = allTeamNumbers
      .filter((num) => {
        const team = teamsByNumber.get(String(num));
        return rowMatchesSearch(num, team);
      })
      .sort((a, b) => {
        const teamA = teamsByNumber.get(String(a));
        const teamB = teamsByNumber.get(String(b));
        let cmp = 0;
        if (sortBy === 'mentor') {
          const nameA = getMentorName(teamA);
          const nameB = getMentorName(teamB);
          if (!nameA && !nameB) cmp = a - b;
          else if (!nameA) cmp = 1;
          else if (!nameB) cmp = -1;
          else cmp = nameA.localeCompare(nameB) || a - b;
        } else {
          cmp = a - b;
        }
        return sortDir === 'desc' ? -cmp : cmp;
      });
    const numberedRows = visibleRows.map((teamNumber) => {
      const team = teamsByNumber.get(String(teamNumber));
      const teamNumKey = String(teamNumber);
      const displayPasscode =
        teamPasscodes[teamNumKey] ??
        (team && (teamPasscodes[team.teamNumber] ?? team.passcode)) ??
        '';
      const mentorLabel = team?.mentor
        ? mentorOptions.find((opt) => opt.value === team.mentor)?.label || team.mentor
        : '';
      const freshlings = Array.isArray(team?.freshlings)
        ? team.freshlings
            .map((email) => {
              const freshling = freshlingOptions.find((opt) => opt.value === email);
              return freshling ? freshling.label : email;
            })
            .join(', ')
        : '';
      return {
        teamNumber: String(teamNumber),
        teamName: team?.teamName ?? '',
        mentor: mentorLabel,
        mentorEmail: (team?.mentor || '').trim(),
        division: team?.division ?? '',
        guest: team?.guest ? 'Yes' : team ? 'No' : '',
        test: team?.test ? 'Test' : team ? '—' : '',
        passcode: displayPasscode,
        freshlings,
      };
    });

    const unnumberedRows = unnumberedTeams
      .filter((team) => rowMatchesSearch('—', team))
      .sort(
        (a, b) =>
          getMentorName(a).localeCompare(getMentorName(b)) ||
          String(a.id || '').localeCompare(String(b.id || ''))
      )
      .map((team) => {
        const normalizedTeamNumber = normalizeTeamNumber(team?.teamNumber);
        const teamNumKey = normalizedTeamNumber || '—';
        const displayPasscode =
          teamPasscodes[teamNumKey] ??
          (team && (teamPasscodes[team.teamNumber] ?? team.passcode)) ??
          '';
        const mentorLabel = team?.mentor
          ? mentorOptions.find((opt) => opt.value === team.mentor)?.label || team.mentor
          : '';
        const freshlings = Array.isArray(team?.freshlings)
          ? team.freshlings
              .map((email) => {
                const freshling = freshlingOptions.find((opt) => opt.value === email);
                return freshling ? freshling.label : email;
              })
              .join(', ')
          : '';
        return {
          teamNumber: normalizedTeamNumber || '—',
          teamName: team?.teamName ?? '',
          mentor: mentorLabel,
          mentorEmail: (team?.mentor || '').trim(),
          division: team?.division ?? '',
          guest: team?.guest ? 'Yes' : team ? 'No' : '',
          test: team?.test ? 'Test' : team ? '—' : '',
          passcode: displayPasscode,
          freshlings,
        };
      });

    return [...numberedRows, ...unnumberedRows];
  }, [
    teams,
    mentorOptions,
    freshlingOptions,
    searchQuery,
    sortBy,
    sortDir,
    teamPasscodes,
  ]);

  const teamMgmtUnregExportRows = useMemo(() => {
    const incompleteTeams = teams.filter(
      (t) => !t.test && (!(t.teamName || '').trim() || !(t.division || '').trim())
    );
    return incompleteTeams
      .filter((t) => (t.mentor || '').trim())
      .map((t) => {
        const opt = mentorOptions.find((o) => o.value === t.mentor);
        const name = opt ? opt.label : t.mentor;
        const missingParts = [];
        if (!(t.teamName || '').trim()) missingParts.push('team name');
        if (!(t.division || '').trim()) missingParts.push('division');
        return {
          mentor: name,
          teamNumber: String(t.teamNumber ?? ''),
          missing: missingParts.join(' & '),
          mentorEmail: (t.mentor || '').trim(),
        };
      })
      .sort((a, b) => {
        const first = (s) => (s || '').split(/\s+/)[0].toLowerCase();
        return first(a.mentor).localeCompare(first(b.mentor)) || a.mentor.localeCompare(b.mentor);
      })
      .map((r, i) => ({ ...r, rowNum: String(i + 1) }));
  }, [teams, mentorOptions]);

  const getTeamMgmtExportColumns = (kind) =>
    kind === 'main' ? TEAM_MANAGEMENT_EXPORT_COLS_MAIN : TEAM_MANAGEMENT_EXPORT_COLS_UNREG;

  useEffect(() => {
    if (!teamMgmtExport) return;
    const cols = getTeamMgmtExportColumns(teamMgmtExport.kind);
    setTeamMgmtExportCols(new Set(cols.filter((c) => c.default).map((c) => c.id)));
  }, [teamMgmtExport]);

  const openTeamMgmtExport = (kind, mode) => {
    const rows = kind === 'main' ? teamMgmtMainExportRows : teamMgmtUnregExportRows;
    if (!rows.length) {
      window.alert(
        kind === 'main'
          ? 'No rows match the current search (or the grid is empty).'
          : 'All teams with mentors have completed registration.'
      );
      return;
    }
    setTeamMgmtExport({ kind, mode });
  };

  const resetTeamMgmtExportCols = () => {
    if (!teamMgmtExport) return;
    const cols = getTeamMgmtExportColumns(teamMgmtExport.kind);
    setTeamMgmtExportCols(new Set(cols.filter((c) => c.default).map((c) => c.id)));
  };

  const runTeamMgmtExport = (selectedColumnIds) => {
    if (!teamMgmtExport) return;
    const { kind, mode } = teamMgmtExport;
    const allCols = getTeamMgmtExportColumns(kind);
    const colSet =
      Array.isArray(selectedColumnIds) && selectedColumnIds.length > 0
        ? new Set(selectedColumnIds)
        : teamMgmtExportCols;
    const active = allCols.filter((c) => colSet.has(c.id));
    if (!active.length) return;
    const baseRows = kind === 'main' ? teamMgmtMainExportRows : teamMgmtUnregExportRows;
    const colMeta = active.map((c) => ({ id: c.id, header: c.label }));
    const rowRecords = baseRows.map((r) => {
      const o = {};
      active.forEach((c) => {
        o[c.id] = r[c.id] ?? '';
      });
      return o;
    });
    const evTitle = activeEvents.find((e) => e.id === selectedEvent)?.title || 'Teams';
    const sub = kind === 'main' ? 'Team roster' : 'Not yet registered';
    if (mode === 'copy') {
      copyText(buildTsvTable(colMeta, rowRecords));
    } else {
      const headerCells = active.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
      const rowsHtml = rowRecords
        .map((rec) => {
          const cells = active.map((c) => `<td>${escapeHtmlCell(rec[c.id])}</td>`).join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      const bodyHtml = `<main style="padding:8px 12px"><h1>${escapeHtmlCell(evTitle)}</h1><p>${escapeHtmlCell(sub)}</p><p>${rowRecords.length} row(s)</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table></main>`;
      openPrintWindow(`${evTitle} — ${sub}`, bodyHtml, getOperationalTablePrintCss());
    }
    setTeamMgmtExport(null);
  };

  const handlePrintUnregistered = async () => {
    const incompleteTeams = teams.filter(
      (t) => !t.test && (!(t.teamName || '').trim() || !(t.division || '').trim())
    );

    const mentorRows = incompleteTeams
      .filter((t) => (t.mentor || '').trim())
      .map((t) => {
        const opt = mentorOptions.find((o) => o.value === t.mentor);
        const name = opt ? opt.label : t.mentor;
        const missingParts = [];
        if (!(t.teamName || '').trim()) missingParts.push('team name');
        if (!(t.division || '').trim()) missingParts.push('division');
        return { name, teamNumber: t.teamNumber, missing: missingParts.join(' & ') };
      })
      .sort((a, b) => {
        const first = (s) => (s || '').split(/\s+/)[0].toLowerCase();
        return first(a.name).localeCompare(first(b.name)) || a.name.localeCompare(b.name);
      });

    if (mentorRows.length === 0) {
      alert('All teams with mentors have completed registration.');
      return;
    }

    const regUrl = 'https://freshmeet-d75a0.web.app/register';
    let qrDataUrl = '';
    try {
      qrDataUrl = await QRCode.toDataURL(regUrl, { width: 200, margin: 1 });
    } catch (err) {
      console.error('QR generation failed:', err);
    }

    const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const tableRows = mentorRows.map((r, i) => `
      <tr>
        <td style="text-align:center">${i + 1}</td>
        <td>${esc(r.name)}</td>
        <td style="text-align:center">${esc(String(r.teamNumber))}</td>
        <td>${esc(r.missing)}</td>
        <td style="width:28px;text-align:center"></td>
      </tr>`).join('');

    const extraCss = `
      h2 { margin: 0 0 4px; font-size: 16pt; }
      .subtitle { color: #555; font-size: 10pt; margin-bottom: 12px; }
      .header-row { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 12px; }
      .header-left { flex: 1; }
      .qr-block { text-align: center; }
      .qr-block img { width: 120px; height: 120px; }
      .qr-block p { margin: 4px 0 0; font-size: 8pt; color: #555; word-break: break-all; }
      th { font-size: 10pt; }
      ${getOperationalTablePrintCss()}
    `;
    const bodyHtml = `
      <div class="header-row">
        <div class="header-left">
          <h2>Teams Not Yet Registered</h2>
          <div class="subtitle">${mentorRows.length} mentor${mentorRows.length === 1 ? '' : 's'} &mdash; please complete registration</div>
        </div>
        ${qrDataUrl ? `<div class="qr-block"><img src="${qrDataUrl}" alt="QR"/><p>${esc(regUrl)}</p></div>` : ''}
      </div>
      <table>
        <thead><tr>
          <th style="width:30px">#</th><th>Mentor</th><th style="width:60px">Team #</th><th>Missing</th><th style="width:28px">&check;</th>
        </tr></thead>
        <tbody>${tableRows}</tbody>
      </table>`;
    openPrintWindow('Unregistered Teams', bodyHtml, extraCss);
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      alert('Choose a CSV file first.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter((line) => line.trim() !== '');

      if (lines.length < 2) {
        alert('CSV file appears to be empty or missing headers.');
        return;
      }

      const delimiter = text.includes('\t') ? '\t' : ',';
      const header = lines[0].split(delimiter).map(normalizeCsvHeaderCell);

      const passcodeIndex = header.indexOf('passcode');
      const emailIndex = header.indexOf('email');
      const teamNumberIndex = findTeamNumberColumnIndex(header);

      if (teamNumberIndex === -1) {
        alert(
          'CSV must have a team column: "Team", "Team Number", "Team Num", or "#".'
        );
        return;
      }

      const hasPasscodeCol = passcodeIndex !== -1;
      const hasEmailCol = emailIndex !== -1;
      if (!hasPasscodeCol && !hasEmailCol) {
        alert('CSV must include a "Passcode" column and/or an "Email" column.');
        return;
      }

      let rowsTouched = 0;
      try {
        const existingTeams = {};
        teams.forEach((t) => {
          existingTeams[t.teamNumber] = t;
        });

        let flyerDict = {};
        if (hasEmailCol) {
          const flyersQuery = query(
            collection(firestore, 'flyers'),
            where('eventId', '==', selectedEvent)
          );
          const flyerSnapshot = await getDocs(flyersQuery);
          flyerSnapshot.forEach((docSnap) => {
            const f = docSnap.data();
            flyerDict[f.email.toLowerCase()] = f; // keyed by lower-case email
          });
        }

        const ensureTeamDoc = async (teamNumber) => {
          let teamDoc = existingTeams[teamNumber];
          if (!teamDoc) {
            teamDoc = {
              teamNumber,
              passcode: '',
              eventId: selectedEvent,
              mentor: '',
              freshlings: [],
              guest: false,
              test: false
            };
            const docRef = await addDoc(collection(firestore, 'teams'), teamDoc);
            teamDoc.id = docRef.id;
            existingTeams[teamNumber] = teamDoc;
            setTeams((prev) => [...prev, teamDoc]);
          }
          return teamDoc;
        };

        for (let i = 1; i < lines.length; i++) {
          const row = lines[i].split(delimiter).map((col) => col.trim());
          const teamNumber = row[teamNumberIndex];
          if (!teamNumber) continue;

          let teamDoc = existingTeams[teamNumber];
          let didUpdate = false;

          if (hasPasscodeCol) {
            const passcode = row[passcodeIndex] || '';
            if (!teamDoc) {
              teamDoc = {
                teamNumber,
                passcode,
                eventId: selectedEvent,
                mentor: '',
                freshlings: [],
                guest: false,
                test: false
              };
              const docRef = await addDoc(collection(firestore, 'teams'), teamDoc);
              teamDoc.id = docRef.id;
              existingTeams[teamNumber] = teamDoc;
              setTeams((prev) => [...prev, teamDoc]);
            } else {
              teamDoc.passcode = passcode;
              const docRef = doc(firestore, 'teams', teamDoc.id);
              await updateDoc(docRef, { passcode });
              setTeams((prev) =>
                prev.map((t) => (t.id === teamDoc.id ? { ...t, passcode } : t))
              );
            }
            didUpdate = true;
          }

          if (hasEmailCol) {
            const email = row[emailIndex]?.toLowerCase();
            if (!email) {
              if (didUpdate) rowsTouched += 1;
              continue;
            }

            teamDoc = await ensureTeamDoc(teamNumber);

            const flyer = flyerDict[email];
            if (!flyer) {
              console.warn(`Flyer not found for email: ${email}`);
              if (didUpdate) rowsTouched += 1;
              continue;
            }

            if (flyer.type === 'mentor') {
              teamDoc.mentor = email;
            } else if (flyer.type === 'freshling') {
              if (!teamDoc.freshlings.includes(email)) {
                teamDoc.freshlings.push(email);
              }
            }

            const docRef = doc(firestore, 'teams', teamDoc.id);
            await updateDoc(docRef, {
              mentor: teamDoc.mentor,
              freshlings: teamDoc.freshlings
            });

            setTeams((prev) =>
              prev.map((t) =>
                t.id === teamDoc.id
                  ? {
                      ...t,
                      mentor: teamDoc.mentor,
                      freshlings: teamDoc.freshlings
                    }
                  : t
              )
            );
            didUpdate = true;
          }

          if (didUpdate) rowsTouched += 1;
        }

        if (rowsTouched === 0) {
          alert('No rows were updated. Check team numbers and column headers.');
        } else {
          alert(`Updated ${rowsTouched} row(s) from the CSV.`);
        }
      } catch (err) {
        console.error('Error processing CSV upload:', err);
        setError('CSV upload failed: ' + (err.message || String(err)));
        alert('CSV upload failed: ' + (err.message || 'Unknown error'));
      }

      setCsvModalOpen(false);
      setCsvFile(null);
    };
    reader.readAsText(csvFile);
  };

  // Upload team passcodes CSV (independent of teams; used for login only)
  const handleUploadPasscodes = () => {
    if (!passcodesFile || !selectedEvent) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = (e.target.result || '').toString();
      const delimiter = text.includes('\t') ? '\t' : ',';
      const lines = text.split('\n').filter((line) => line.trim() !== '');
      if (lines.length < 2) {
        alert('CSV needs a header row and at least one data row.');
        return;
      }
      const header = lines[0].split(delimiter).map(normalizeCsvHeaderCell);
      const teamNumCol = findTeamNumberColumnIndex(header);
      const wordCol = header.findIndex((h) => h === 'word' || h === 'passcode');
      if (teamNumCol === -1 || wordCol === -1) {
        alert(
          'CSV must have a team column ("Team", "Team Number", "Team Num", or "#") and a passcode column ("Passcode" or "Word").'
        );
        return;
      }
      setUploadingPasscodes(true);
      try {
        const passcodes = {};
        for (let i = 1; i < lines.length; i++) {
          const row = lines[i].split(delimiter).map((c) => (c || '').trim());
          const num = (row[teamNumCol] || '').toString().trim();
          const word = (row[wordCol] || '').toString().trim();
          if (num) passcodes[num] = word;
        }
        await setDoc(doc(firestore, 'teamPasscodes', selectedEvent), { passcodes }, { merge: false });
        setTeamPasscodes(passcodes);
        // Keep team documents in sync: same passcode in team.passcode so there is one source of truth
        const teamsQuery = query(collection(firestore, 'teams'), where('eventId', '==', selectedEvent));
        const teamsSnap = await getDocs(teamsQuery);
        for (const docSnap of teamsSnap.docs) {
          const team = docSnap.data();
          const numKey = String(team.teamNumber ?? '').trim();
          if (numKey && passcodes[numKey] !== undefined) {
            await updateDoc(doc(firestore, 'teams', docSnap.id), { passcode: passcodes[numKey] });
          }
        }
        setTeams((prev) =>
          prev.map((t) => {
            const numKey = String(t.teamNumber ?? '').trim();
            const code = passcodes[numKey];
            return code !== undefined ? { ...t, passcode: code } : t;
          })
        );
        setPasscodesFile(null);
        if (passcodesFileInputRef.current) passcodesFileInputRef.current.value = '';
        alert(`Saved ${Object.keys(passcodes).length} passcode(s) for this event.`);
      } catch (err) {
        setError('Failed to save passcodes: ' + (err.message || ''));
      } finally {
        setUploadingPasscodes(false);
      }
    };
    reader.readAsText(passcodesFile);
  };

  const handlePasscodesUploadButtonClick = () => {
    if (!selectedEvent || uploadingPasscodes) return;
    if (!passcodesFile) {
      passcodesFileInputRef.current?.click();
      return;
    }
    handleUploadPasscodes();
  };

  const resetNewTeamForm = () => {
    setEditingTeamId(null);
    setNewTeam({
      teamNumber: '',
      teamName: '',
      mentor: '',
      division: '',
      guest: false,
      test: false,
      passcode: '',
      freshlings: [],
    });
  };

  const closeTeamModal = () => {
    resetNewTeamForm();
    setModalOpen(false);
  };

  useModalEscapeKey(modalOpen, closeTeamModal);
  useModalEscapeKey(!!registrationReminderModal, () => setRegistrationReminderModal(null));
  useModalEscapeKey(csvModalOpen, () => setCsvModalOpen(false));

  const handleOpenNewTeam = () => {
    resetNewTeamForm();
    setModalOpen(true);
  };

  const handleOpenNewTeamForSlot = (slotTeamNumber) => {
    setEditingTeamId(null);
    setNewTeam({
      teamNumber: String(slotTeamNumber),
      teamName: '',
      mentor: '',
      division: '',
      guest: false,
      test: false,
      passcode: '',
      freshlings: [],
    });
    setModalOpen(true);
  };

  return (
    <div className="team-management">
      <h2>Teams</h2>
      {error && <p className="error">{error}</p>}
      <div className="dropdown-container">
        <label htmlFor="activeEvent">Select Event:</label>
        <select id="activeEvent" value={selectedEvent} onChange={e => {
          setSelectedEvent(e.target.value);
          setLoading(true);
        }}>
          {activeEvents.map(event => (
            <option key={event.id} value={event.id}>{event.title}</option>
          ))}
        </select>
      </div>
      <div className="button-group">
        <span className="team-pairing-hint">Create teams in Team Pairing.</span>
        <button
          type="button"
          className="add-team-button"
          onClick={handleOpenNewTeam}
          disabled={!selectedEvent || loading}
        >
          Add Team
        </button>
        <button
          type="button"
          className="upload-csv-button"
          onClick={() => setCsvModalOpen(true)}
          disabled={!selectedEvent || loading}
        >
          Upload CSV
        </button>
        <button className="delete-all-button" onClick={handleDeleteAll}>Delete All</button>
        <button type="button" className="delete-test-data-button" onClick={handleDeleteAllTestData} disabled={!selectedEvent}>
          Delete all test data
        </button>
        <button
          type="button"
          className="print-teams-button"
          onClick={() => openTeamMgmtExport('main', 'print')}
          disabled={loading}
        >
          Print table
        </button>
        <AdminCopyMenu
          ariaLabel="Copy team table or unregistered list"
          disabled={loading}
          items={[
            {
              label: 'Copy table…',
              disabled: !teamMgmtMainExportRows.length,
              onSelect: () => openTeamMgmtExport('main', 'copy'),
              showCopiedFeedback: false,
            },
            {
              label: 'Copy unregistered table…',
              disabled: !teamMgmtUnregExportRows.length,
              onSelect: () => openTeamMgmtExport('unreg', 'copy'),
              showCopiedFeedback: false,
            },
          ]}
        />
        <button
          type="button"
          className="print-teams-button"
          onClick={() => openTeamMgmtExport('unreg', 'print')}
          disabled={!selectedEvent || loading || !teamMgmtUnregExportRows.length}
        >
          Print unregistered table
        </button>
        <button
          type="button"
          className="print-teams-button"
          onClick={handlePrintUnregistered}
          disabled={!selectedEvent || loading}
        >
          Print Unregistered (QR)
        </button>
        <button
          type="button"
          className="registration-reminder-button"
          onClick={openRegistrationReminderModal}
          disabled={!selectedEvent || loading}
        >
          Send Registration Reminder
        </button>
      </div>

      <section className="team-passcodes-section">
        <div className="team-passcodes-upload">
          <span className="team-passcodes-label">Passcodes (login):</span>
          <input
            ref={passcodesFileInputRef}
            type="file"
            accept=".csv"
            className="team-passcodes-file-input-hidden"
            aria-hidden="true"
            tabIndex={-1}
            onChange={(e) => setPasscodesFile(e.target.files?.[0] || null)}
          />
          {passcodesFile ? (
            <span className="team-passcodes-filename" title={passcodesFile.name}>
              {passcodesFile.name}
            </span>
          ) : null}
          <button
            type="button"
            className="upload-passcodes-button"
            onClick={handlePasscodesUploadButtonClick}
            disabled={!selectedEvent || uploadingPasscodes}
            title={
              passcodesFile
                ? 'Upload the selected CSV to passcodes for this event'
                : 'Choose a CSV file, then click again to upload'
            }
          >
            {uploadingPasscodes ? 'Uploading…' : 'Upload CSV'}
          </button>
        </div>
      </section>
      
      {/* Search box */}
      <div className="search-container">
        <label htmlFor="teamSearch">Search Teams:</label>
        <input
          id="teamSearch"
          type="text"
          placeholder="Search by team name, mentor name, or freshling name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="table-container">
        {loading ? (
          <p>Loading teams...</p>
        ) : (() => {
          const TEAM_COUNT = 60;
          const allTeamNumbers = Array.from({ length: TEAM_COUNT }, (_, i) => i + 1);
          const normalizeTeamNumber = (value) => String(value ?? '').trim();
          const numberedRangeSet = new Set(allTeamNumbers.map((n) => String(n)));

          const rowMatchesSearch = (teamNumber, team) => {
            if (!searchQuery.trim()) return true;
            const query = searchQuery.toLowerCase().trim();
            if (!team) return String(teamNumber).includes(query);
            const teamNameMatch = (team.teamName || '').toLowerCase().includes(query);
            const mentorOption = mentorOptions.find(opt => opt.value === team.mentor);
            const mentorName = mentorOption ? mentorOption.label : '';
            const mentorMatch = mentorName.toLowerCase().includes(query);
            const freshlingNames = Array.isArray(team.freshlings)
              ? team.freshlings.map(email => {
                  const freshlingOption = freshlingOptions.find(opt => opt.value === email);
                  return freshlingOption ? freshlingOption.label : email;
                }).join(' ')
              : '';
            const freshlingMatch = freshlingNames.toLowerCase().includes(query);
            return teamNameMatch || mentorMatch || freshlingMatch || String(teamNumber).includes(query);
          };

          const getMentorName = (team) => {
            if (!team?.mentor) return '';
            const opt = mentorOptions.find(o => o.value === team.mentor);
            return (opt ? opt.label : team.mentor).toLowerCase();
          };

          const teamsByNumber = new Map();
          const unnumberedTeams = [];
          teams.forEach((team) => {
            const normalized = normalizeTeamNumber(team?.teamNumber);
            if (numberedRangeSet.has(normalized)) {
              if (!teamsByNumber.has(normalized)) teamsByNumber.set(normalized, team);
            } else {
              unnumberedTeams.push(team);
            }
          });

          const visibleRows = allTeamNumbers
            .map((num) => {
              const team = teamsByNumber.get(String(num)) || null;
              return { rowType: 'numbered', teamNumber: num, team };
            })
            .filter(({ teamNumber, team }) => rowMatchesSearch(teamNumber, team))
            .sort((a, b) => {
              let cmp = 0;
              if (sortBy === 'mentor') {
                const nameA = getMentorName(a.team);
                const nameB = getMentorName(b.team);
                if (!nameA && !nameB) cmp = a.teamNumber - b.teamNumber;
                else if (!nameA) cmp = 1;
                else if (!nameB) cmp = -1;
                else cmp = nameA.localeCompare(nameB) || a.teamNumber - b.teamNumber;
              } else {
                cmp = a.teamNumber - b.teamNumber;
              }
              return sortDir === 'desc' ? -cmp : cmp;
            });

          const visibleUnnumberedRows = unnumberedTeams
            .filter((team) => rowMatchesSearch('—', team))
            .sort((a, b) => getMentorName(a).localeCompare(getMentorName(b)) || String(a.id || '').localeCompare(String(b.id || '')))
            .map((team) => ({
              rowType: 'unnumbered',
              teamNumber: normalizeTeamNumber(team?.teamNumber) || '—',
              team,
            }));

          const rowsToRender = [...visibleRows, ...visibleUnnumberedRows];

          const sortIndicator = (col) =>
            sortBy === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

          return (
            <table className="team-table">
              <thead>
                <tr>
                  <th className="sortable-header" onClick={() => handleSort('number')}>
                    Team Number{sortIndicator('number')}
                  </th>
                  <th>Team Name</th>
                  <th className="sortable-header" onClick={() => handleSort('mentor')}>
                    Mentor{sortIndicator('mentor')}
                  </th>
                  <th>Division</th>
                  <th>Guest</th>
                  <th>Test</th>
                  <th>Passcode</th>
                  <th>Freshlings</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rowsToRender.map(({ rowType, teamNumber, team }) => {
                  const teamNumKey = String(team?.teamNumber ?? teamNumber);
                  const displayPasscode = teamPasscodes[teamNumKey] ?? (team && (teamPasscodes[team.teamNumber] ?? team.passcode)) ?? '';
                  const freshlingCount = team?.freshlings ? team.freshlings.length : 0;
                  const highlightClass = freshlingCount !== 2 && team ? 'highlight-row' : '';

                  return (
                    <tr key={team?.id || `${rowType}-${teamNumber}`} className={`${highlightClass} ${team?.test ? 'team-row--test' : ''}`}>
                      <td>{teamNumber}</td>
                      <td>{team?.teamName ?? ''}</td>
                      <td>
                        {team?.mentor
                          ? mentorOptions.find(opt => opt.value === team.mentor)?.label || team.mentor
                          : ''}
                      </td>
                      <td>{team?.division ?? ''}</td>
                      <td>{team?.guest ? 'True' : (team ? 'False' : '')}</td>
                      <td>{team?.test ? 'Test' : (team ? '—' : '')}</td>
                      <td>{displayPasscode}</td>
                      <td>
                        {Array.isArray(team?.freshlings)
                          ? team.freshlings.map(email => {
                              const freshling = freshlingOptions.find(opt => opt.value === email);
                              return freshling ? freshling.label : email;
                            }).join(', ')
                          : ''}
                      </td>
                      <td>
                        {team ? (
                          <>
                            <button type="button" className="btn-fm-primary" onClick={() => handleEdit(team)}>Edit</button>
                            <button type="button" className="team-table-btn-delete" onClick={() => handleDelete(team.id)}>Delete</button>
                          </>
                        ) : rowType === 'numbered' ? (
                          <span className="team-table-empty-actions">
                            <button
                              type="button"
                              className="row-add-button"
                              onClick={() => handleOpenNewTeamForSlot(teamNumber)}
                            >
                              Add
                            </button>
                            <span className="team-pairing-hint">Use Team Pairing</span>
                          </span>
                        ) : (
                          <span className="team-pairing-hint">Use Team Pairing</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          );
        })()}
      </div>

      {/* Modal for add/edit team */}
      {modalOpen && (
        <div className="fm-modal-overlay" role="presentation" onClick={closeTeamModal}>
          <div
            className="fm-modal fm-modal--md"
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-mgmt-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="team-mgmt-modal-title">
                {editingTeamId ? 'Edit team' : 'Add team'}
              </h2>
              <button type="button" className="fm-modal-close" onClick={closeTeamModal} aria-label="Close">
                ✕
              </button>
            </div>
            <form noValidate onSubmit={handleSubmit} className="team-form">
              <div className="fm-modal-body">
              <div className="form-group">
                <label>Team Number*:</label>
                <input
                  name="teamNumber"
                  type="text"
                  value={newTeam.teamNumber}
                  onChange={(e) => setNewTeam({ ...newTeam, teamNumber: e.target.value })}
                  required
                />
                <ModalFieldRequiredError invalidNames={teamFormInvalidNames} fieldName="teamNumber" />
              </div>
              <div className="form-group">
                <label>Team Name:</label>
                <input
                  type="text"
                  value={newTeam.teamName}
                  onChange={(e) => setNewTeam({ ...newTeam, teamName: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Mentor:</label>
                <label className="team-mgmt-mentor-filter">
                  <input
                    type="checkbox"
                    checked={showAllMentorFlyers}
                    onChange={(e) => setShowAllMentorFlyers(e.target.checked)}
                  />
                  Show all mentor flyers (include pending / excluded)
                </label>
                <Select
                  options={mentorOptions}
                  value={mentorOptions.find(opt => opt.value === newTeam.mentor) || null}
                  onChange={(selectedOption) =>
                    setNewTeam({ ...newTeam, mentor: selectedOption ? selectedOption.value : '' })
                  }
                  isClearable
                  placeholder="Select mentor..."
                />
              </div>
              <div className="form-group">
                <label>Division:</label>
                <input
                  type="text"
                  value={newTeam.division}
                  onChange={(e) => setNewTeam({ ...newTeam, division: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Guest (ineligible to medal):</label>
                <select
                  value={newTeam.guest ? 'true' : 'false'}
                  onChange={(e) => setNewTeam({ ...newTeam, guest: e.target.value === 'true' })}
                >
                  <option value="false">False</option>
                  <option value="true">True</option>
                </select>
              </div>
              <div className="form-group">
                <label>Test (dummy data for pre-event testing):</label>
                <select
                  value={newTeam.test ? 'true' : 'false'}
                  onChange={(e) => setNewTeam({ ...newTeam, test: e.target.value === 'true' })}
                >
                  <option value="false">False</option>
                  <option value="true">True</option>
                </select>
              </div>
              <div className="form-group">
                <label>Passcode*:</label>
                <input
                  name="teamPasscode"
                  type="text"
                  value={newTeam.passcode}
                  onChange={(e) => setNewTeam({ ...newTeam, passcode: e.target.value })}
                  required
                />
                <ModalFieldRequiredError invalidNames={teamFormInvalidNames} fieldName="teamPasscode" />
              </div>
              <div className="form-group">
                <label>Freshlings (comma-separated):</label>
                <Select
  options={freshlingOptions}
  value={
    Array.isArray(newTeam.freshlings) && newTeam.freshlings.length > 0
      ? freshlingOptions.filter(opt =>
          newTeam.freshlings.includes(opt.value)
        )
      : []
  }
  onChange={(selectedOptions) =>
    setNewTeam({
      ...newTeam,
      freshlings:
        selectedOptions && selectedOptions.length > 0
          ? selectedOptions.map(opt => opt.value)
          : []
    })
  }
  isMulti
  isClearable
  placeholder="Select freshlings..."
/>

              </div>
              </div>
              <div className="fm-modal-footer">
                <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={closeTeamModal}>
                  Cancel
                </button>
                <button type="submit" className="fm-modal-btn fm-modal-btn-primary">
                  {editingTeamId ? 'Update team' : 'Add team'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {registrationReminderModal && (
        <div
          className="fm-modal-overlay"
          role="presentation"
          onClick={() => setRegistrationReminderModal(null)}
        >
          <div
            className="fm-modal fm-modal--md"
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-reg-reminder-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="team-reg-reminder-title">
                Send registration reminder?
              </h2>
              <button
                type="button"
                className="fm-modal-close"
                onClick={() => setRegistrationReminderModal(null)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="fm-modal-body">
            <p>
              This will open the email composer with{' '}
              <strong>{registrationReminderModal.mentorEmailCount}</strong> mentor
              {registrationReminderModal.mentorEmailCount === 1 ? '' : 's'} in BCC (
              <strong>{registrationReminderModal.incompleteTeamCount}</strong> incomplete team
              {registrationReminderModal.incompleteTeamCount === 1 ? '' : 's'}).
            </p>
            {registrationReminderModal.summaryParts.length > 0 ? (
              <ul className="registration-reminder-modal-summary">
                {registrationReminderModal.summaryParts.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : null}
            </div>
            <div className="fm-modal-footer">
              <button
                type="button"
                className="fm-modal-btn fm-modal-btn-cancel"
                onClick={() => setRegistrationReminderModal(null)}
              >
                Cancel
              </button>
              <button type="button" className="fm-modal-btn fm-modal-btn-primary" onClick={confirmRegistrationReminder}>
                Open email composer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for CSV upload */}
      {csvModalOpen && (
        <div className="fm-modal-overlay" role="presentation" onClick={() => setCsvModalOpen(false)}>
          <div
            className="fm-modal fm-modal--sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-csv-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="team-csv-modal-title">
                Upload teams via CSV
              </h2>
              <button type="button" className="fm-modal-close" onClick={() => setCsvModalOpen(false)} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="fm-modal-body">
            <div className="form-group">
              <label>Select CSV File:</label>
              <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files[0])} />
            </div>
            </div>
            <div className="fm-modal-footer">
              <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={() => setCsvModalOpen(false)}>
                Cancel
              </button>
              <button type="button" className="fm-modal-btn fm-modal-btn-primary" onClick={handleCsvUpload}>
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {teamMgmtExport && (
        <AdminColumnExportModal
          mode={teamMgmtExport.mode}
          columns={getTeamMgmtExportColumns(teamMgmtExport.kind)}
          selectedIds={teamMgmtExportCols}
          onChangeSelected={setTeamMgmtExportCols}
          onCancel={() => setTeamMgmtExport(null)}
          onConfirm={runTeamMgmtExport}
          onResetDefaults={resetTeamMgmtExportCols}
        />
      )}

      <EmailComposerModal
        isOpen={emailComposerProps.isOpen}
        context={emailComposerProps.context}
        initialRecipients={emailComposerProps.initialRecipients}
        initialTemplate={emailComposerProps.initialTemplate}
        initialSubject={emailComposerProps.initialSubject}
        initialBody={emailComposerProps.initialBody}
        onClose={closeEmailComposer}
      />
    </div>
  );
};

export default TeamManagement;
